import React from 'react';
import Wahanda from 'common/wahanda';
import moment from 'common/moment';
import Dialog from 'components/common/react-dialog';
import { Notice } from 'components/common/Notice';
import { Button } from 'components/common/Button';
import { PaymentError, PaymentSuccess } from 'components/common/Icon';
import { FinancePaymentsTracking } from './tracking';
import { StripeProvider } from './PaymentContainer/Stripe';
import { PaymentContainer } from './PaymentContainer';
import { Reconnect } from 'federation/Reconnect';
import { CompletePage } from './PaymentContainer/StripeCheckoutNewFlow/CompletePage';
import { PaymentStatus } from './types';
import style from './PaymentMessage.scss';
import {
  isRedirectFromStripe,
  isRedirectFromStripeOCS,
  getStripeOCSRedirectStatus,
  stripQueryParams,
  StripeOCSRedirectStatus,
} from 'components/settings/PaymentMessage/utils';

const lang = Wahanda.lang.settings.supplier.payments;

// Direct Debit first Pay now experiment (PAYB2B-939): same supplier-or-country convention as the
// invoice direct checkout flags.
const DIRECT_DEBIT_FIRST_BY_SUPPLIER_FF = 'PAYB2B-939-direct-debit-first-pay-now-supplier';
const DIRECT_DEBIT_FIRST_BY_COUNTRY_FF = 'PAYB2B-939-direct-debit-first-pay-now-country';

// Direct Debit entry point where no Pay now is shown (PAYB2B-949): its own flags, same convention, so it
// rolls out apart from the Pay now experiment.
const DIRECT_DEBIT_ENTRY_BY_SUPPLIER_FF = 'PAYB2B-949-direct-debit-entry-supplier';
const DIRECT_DEBIT_ENTRY_BY_COUNTRY_FF = 'PAYB2B-949-direct-debit-entry-country';

// After Turn on Direct Debit, Pay now stays hidden for 48h on this browser, per supplier.
const DIRECT_DEBIT_SET_UP_AT_KEY = 'PAYB2B-939-direct-debit-set-up-at';
const PAY_NOW_HIDDEN_AFTER_SET_UP_MS = 48 * 60 * 60 * 1000;

const getDirectDebitSetUpKey = (supplierId: number) =>
  `${DIRECT_DEBIT_SET_UP_AT_KEY}-${supplierId}`;

const isPayNowHiddenAfterSetUp = (supplierId: number): boolean => {
  try {
    const setUpAt = Number(localStorage.getItem(getDirectDebitSetUpKey(supplierId)));
    return setUpAt > 0 && Date.now() - setUpAt < PAY_NOW_HIDDEN_AFTER_SET_UP_MS;
  } catch {
    return false;
  }
};

const rememberDirectDebitSetUp = (supplierId: number) => {
  try {
    localStorage.setItem(getDirectDebitSetUpKey(supplierId), String(Date.now()));
  } catch {
    // Storage unavailable (private mode, quota): Pay now simply stays visible.
  }
};

export interface Props {
  actions: {
    requestBankDetailsAction: (supplierId: number) => void;
    requestLastPaymentStatus: (supplierId: number) => void;
  };
  lastPayment?: {
    status: PaymentStatus;
    date: string;
    amount: number;
    id: number;
  };
  supplierId: number;
  closingBalance: number;
  reFetchRemittances: () => void;
  paymentDueDays: number;
  paymentReference: string;
  paymentId: number;
  autoOpenPaymentForm: boolean;
  // Direct Debit partners reach Pay now only from the debt-campaign email (?showPayNow).
  hasDirectDebitEnabled?: boolean;
}

interface State {
  isPaymentDialogOpen: boolean;
  isStripeRedirectModalOpen: boolean;
  isDirectDebitPromoOpen: boolean;
  ocsRedirectStatus: StripeOCSRedirectStatus | null;
}

export class PaymentMessage extends React.PureComponent<Props, State> {
  public static defaultProps = {
    autoOpenPaymentForm: false,
    lastPayment: undefined,
  };

  public state: State = {
    // Balance check matches renderPayUsMessage: the emailed link can be opened after
    // the balance was already collected, and that partner owes nothing.
    isPaymentDialogOpen: this.props.autoOpenPaymentForm && this.props.closingBalance > 0,
    isStripeRedirectModalOpen: false,
    isDirectDebitPromoOpen: false,
    ocsRedirectStatus: null,
  };

  public componentDidUpdate(prevProps: Props) {
    const { closingBalance, lastPayment } = this.props;
    if (
      prevProps.lastPayment == null &&
      lastPayment != null &&
      lastPayment.status === PaymentStatus.SUCCESSFUL &&
      moment(lastPayment.date).isAfter(moment().subtract(1, 'minutes')) &&
      closingBalance > 0
    ) {
      FinancePaymentsTracking.trackInconsistentData();
    }
  }

  public componentDidMount() {
    const { supplierId } = this.props;
    this.props.actions.requestBankDetailsAction(supplierId);
    this.props.actions.requestLastPaymentStatus(supplierId);

    const search = window.location.search;

    if (isRedirectFromStripeOCS(search)) {
      this.setState({
        ocsRedirectStatus: getStripeOCSRedirectStatus(search),
      });
    } else if (isRedirectFromStripe(search)) {
      this.setState({
        isStripeRedirectModalOpen: true,
      });
    }
  }

  // Same permissions the backend requires to change bank details, where Direct Debit is turned on.
  private canChangeBankDetails(): boolean {
    return Wahanda.Permissions.manageVenueDetails() && Wahanda.Permissions.viewFinanceData();
  }

  private isDirectDebitPromoEnabled(): boolean {
    if (this.props.hasDirectDebitEnabled || !this.canChangeBankDetails()) {
      return false;
    }
    return (
      Wahanda.Features.isEnabled(DIRECT_DEBIT_FIRST_BY_SUPPLIER_FF) ||
      Wahanda.Features.isEnabled(DIRECT_DEBIT_FIRST_BY_COUNTRY_FF)
    );
  }

  // Same eligibility as the Pay now promo, and hidden for the same 48h after Turn on Direct Debit.
  private isDirectDebitPromoEntryEnabled(): boolean {
    if (
      this.props.hasDirectDebitEnabled ||
      !this.canChangeBankDetails() ||
      isPayNowHiddenAfterSetUp(this.props.supplierId)
    ) {
      return false;
    }
    return (
      Wahanda.Features.isEnabled(DIRECT_DEBIT_ENTRY_BY_SUPPLIER_FF) ||
      Wahanda.Features.isEnabled(DIRECT_DEBIT_ENTRY_BY_COUNTRY_FF)
    );
  }

  public handleClick = () => {
    FinancePaymentsTracking.trackMakePaymentClick();
    if (this.isDirectDebitPromoEnabled()) {
      this.setState({ isDirectDebitPromoOpen: true });
      return;
    }
    this.setState({
      isPaymentDialogOpen: true,
    });
  };

  public handleDirectDebitPromoClose = () => {
    this.setState({ isDirectDebitPromoOpen: false });
  };

  // Card and bank transfer are the existing payment options, opened from the promo.
  public handleDirectDebitPromoPayNow = () => {
    this.setState({ isDirectDebitPromoOpen: false, isPaymentDialogOpen: true });
  };

  // The modal makes no API call: Turn on Direct Debit opens the calendar with the "Direct Debit is on"
  // confirmation (?dd-enabled=1, PAYB2B-760). Enablement is measured by the promo's Mixpanel event.
  public handleDirectDebitPromoSetUp = () => {
    rememberDirectDebitSetUp(this.props.supplierId);
    this.handleDirectDebitPromoClose();
    window.location.assign('./calendar?dd-enabled=1');
  };

  // One object for the component's lifetime: Reconnect re-injects an island whenever its props change
  // identity, so a new object on each re-render (as when the bank details and last payment load) would
  // remount the entry point and track a second impression.
  private readonly directDebitPromoEntryProps = { onSetUp: this.handleDirectDebitPromoSetUp };

  public renderDirectDebitPromo() {
    const { closingBalance } = this.props;
    if (!this.state.isDirectDebitPromoOpen) {
      return null;
    }

    return (
      <Reconnect
        name="DirectDebitPromoModal"
        props={{
          openInvoiceAmount: Wahanda.Currency.format(closingBalance),
          onSetUp: this.handleDirectDebitPromoSetUp,
          onDismiss: this.handleDirectDebitPromoClose,
          onPayByCard: this.handleDirectDebitPromoPayNow,
          onShowBankTransfer: this.handleDirectDebitPromoPayNow,
        }}
      />
    );
  }

  // Offers Direct Debit where no Pay now is shown, behind its own flags.
  public renderDirectDebitPromoEntry() {
    if (this.props.closingBalance > 0 || !this.isDirectDebitPromoEntryEnabled()) {
      return null;
    }

    return (
      <div className={style.message}>
        <Reconnect name="DirectDebitPromoEntry" props={this.directDebitPromoEntryProps} />
      </div>
    );
  }

  public handlePaymentDialogClose = () => {
    this.setState({
      isPaymentDialogOpen: false,
    });
  };

  public renderPaymentDialog() {
    const { isPaymentDialogOpen } = this.state;
    const { closingBalance, paymentReference, paymentId } = this.props;

    if (!isPaymentDialogOpen) {
      return null;
    }

    return (
      <StripeProvider closingBalance={closingBalance}>
        <PaymentContainer
          paymentReference={paymentReference}
          closingBalance={closingBalance}
          onClose={this.handlePaymentDialogClose}
          paymentId={paymentId}
        />
      </StripeProvider>
    );
  }

  public renderPayUsMessage() {
    const { closingBalance, paymentDueDays } = this.props;
    if (closingBalance <= 0) {
      return null;
    }

    const boldMessage = Wahanda.Template.render(lang.remittanceBalanceNote, {
      closingBalance: Wahanda.Currency.format(closingBalance),
    });

    const dayString = lang.remittanceDay ? lang.remittanceDays : null;
    const daysString = lang.remittanceDays ? lang.remittanceDays : null;

    const dueDays = `${paymentDueDays} ${paymentDueDays === 1 ? dayString : daysString}`;
    const remittanceNumberUnpaidNote = Wahanda.Template.render(lang.remittanceNumberUnpaidNote, {
      dueDateDays: dueDays,
    });

    return (
      <div className={style.message}>
        <Notice dataTest="pay-us-message" type="alert">
          <div className={style.attention}>
            <strong>{boldMessage}</strong>
            <div>{remittanceNumberUnpaidNote}</div>
            {!isPayNowHiddenAfterSetUp(this.props.supplierId) && (
              <div className={style.button}>
                <Button label={lang.payNowButton} onClick={this.handleClick} />
              </div>
            )}
          </div>
        </Notice>
      </div>
    );
  }

  public renderPaymentStatusMessage() {
    const { lastPayment } = this.props;
    if (!lastPayment) {
      return null;
    }
    const isTerminalStatus =
      lastPayment.status === PaymentStatus.SUCCESSFUL ||
      lastPayment.status === PaymentStatus.FAILED;
    if (isTerminalStatus && moment(lastPayment.date).isBefore(moment().subtract(1, 'day'))) {
      return null;
    }
    const { amount, date, id } = lastPayment;

    switch (lastPayment.status) {
      case PaymentStatus.SUCCESSFUL:
        return (
          <Notice dataTest="payment-succeeded-message" type="success">
            <div
              dangerouslySetInnerHTML={{
                __html: Wahanda.Template.render(lang.paymentStatus.complete, {
                  date: moment(date).formatDateDefault(),
                  id,
                  amount: Wahanda.Currency.format(amount),
                }),
              }}
            />
          </Notice>
        );
      case PaymentStatus.PENDING:
        return (
          <Notice dataTest="payment-ongoing-message">
            <div
              dangerouslySetInnerHTML={{
                __html: Wahanda.Template.render(lang.paymentStatus.pending, {
                  date: moment(date).formatDateDefault(),
                  id,
                  amount: Wahanda.Currency.format(amount),
                }),
              }}
            />
          </Notice>
        );
      case PaymentStatus.FAILED:
        return (
          <Notice dataTest="payment-failed-message" type="alert">
            <div
              dangerouslySetInnerHTML={{
                __html: Wahanda.Template.render(lang.paymentStatus.failed, {
                  date: moment(date).formatDateDefault(),
                  id,
                }),
              }}
            />
          </Notice>
        );
      default:
        return null;
    }
  }

  public handleCompletePageClose = () => {
    this.setState({
      isStripeRedirectModalOpen: false,
    });
    this.cleanUrlParams();
  };

  public handleOCSStatusClose = () => {
    this.setState({
      ocsRedirectStatus: null,
    });
    this.cleanUrlParams();
  };

  private cleanUrlParams() {
    try {
      const cleanUrl = stripQueryParams(window.location.href);
      window.history.replaceState(null, '', cleanUrl);
    } catch (error) {
      console.log(error);
    }
  }

  public renderStripePaymentStatus() {
    const { isStripeRedirectModalOpen } = this.state;

    if (!isStripeRedirectModalOpen) {
      return null;
    }

    return (
      <CompletePage supplierId={this.props.supplierId} onClose={this.handleCompletePageClose} />
    );
  }

  public renderOCSPaymentStatus() {
    const { ocsRedirectStatus } = this.state;

    if (!ocsRedirectStatus) {
      return null;
    }

    const isSuccess = ocsRedirectStatus === 'success';

    return (
      <Dialog
        dataTest="ocs-payment-status-dialog"
        title={lang.ocsPaymentStatus.title}
        onClose={this.handleOCSStatusClose}
        width={420}
      >
        <div className={style.ocsContainer}>
          <div className={style.ocsContent}>
            <div
              className={isSuccess ? style.ocsIconSuccess : style.ocsIconError}
              data-test={isSuccess ? 'ocs-status-success-icon' : 'ocs-status-error-icon'}
            >
              {isSuccess ? <PaymentSuccess size="large" /> : <PaymentError size="large" />}
            </div>
            <div className={style.ocsMessage}>
              {isSuccess ? lang.ocsPaymentStatus.success : lang.ocsPaymentStatus.error}
            </div>
          </div>
          <div className={style.ocsActions}>
            <Button
              label={Wahanda.lang.shared.ok}
              onClick={this.handleOCSStatusClose}
              dataTest="ocs-status-ok"
              fullWidth
              size="large"
            />
          </div>
        </div>
      </Dialog>
    );
  }

  public render() {
    return (
      <div>
        {this.renderPaymentDialog()}
        {this.renderDirectDebitPromo()}
        {this.renderPayUsMessage()}
        {this.renderDirectDebitPromoEntry()}
        {this.renderPaymentStatusMessage()}
        {this.renderStripePaymentStatus()}
        {this.renderOCSPaymentStatus()}
      </div>
    );
  }
}
